import React, {useEffect, useMemo} from 'react';
import {Grid} from '@material-ui/core';
import classNames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import {ReduxState} from '../../store/types';
import {actions} from '../../store';
import {ServiceFeature, ServiceFeatureName,} from '../../store/types/ServiceFeature';
import {DialingCodes} from '../../store/types/DialingRule';
import {AccountPermission, PermissionType} from '../../store/types/Permission';

import Header from '../../components/ListViewHeader/Header';
import Loader from '../../components/Loader/Loader';
import InfoBanner from '../../components/InfoBanner/InfoBanner';
import RuleInput from '../../components/Forms/CallSettings/utils/RuleInput';
import SwitchWithLabel from '../../components/SwitchWithLabel/SwitchWithLabel';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';
import AccessDenied from '../../components/PermissionProvider/AccessDenied';
import {usePermissions} from '../../hooks/usePermissions';

import AccountControlPanel from '../AccountControlPanel/AccountControlPanel';
import {blurNoop, findFeature, isOnDemandRecording, useStyles} from './AccountServiceCodes.utils';

const AccountServiceCodes: React.VFC = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const permission = usePermissions(...AccountPermission.ServiceCodes.value);

    const {
        isLoadingServiceCodes,
        isUpdatingServiceCodesFeature,
        accountDialingRule,
        accountServiceCodesFeatures,
    } = useSelector((state: ReduxState) => state.accountControlPanel);

    useEffect(() => {
        if (permission !== PermissionType.Hidden) {
            dispatch(actions.getAccountServiceCodes.request());
        }
    }, [permission]);

    const dialCodes: Partial<DialingCodes> = useMemo(
        () => accountDialingRule?.dial_codes ?? {},
        [accountDialingRule],
    );

    const clirFeature = findFeature(
        accountServiceCodesFeatures,
        ServiceFeatureName.Clir,
    );
    const callRecordingFeature = findFeature(
        accountServiceCodesFeatures,
        ServiceFeatureName.CallRecording,
    );
    const callProcessingFeature = findFeature(
        accountServiceCodesFeatures,
        ServiceFeatureName.CallProcessing,
    );

    const clirOn = clirFeature?.effective_flag_value === 'Y';
    const callRecordingOn = isOnDemandRecording(callRecordingFeature);
    const callProcessingOn =
        callProcessingFeature?.effective_flag_value === 'Y';

    const isReadOnlyPermission = permission === PermissionType.ReadOnly;

    const isToggleDisabled = (feature?: ServiceFeature) =>
        !feature ||
        feature.locked !== 0 ||
        isReadOnlyPermission ||
        !!isUpdatingServiceCodesFeature;

    const onToggleFeature = (
        featureName: ServiceFeatureName,
        newValue: boolean,
    ) => {
        dispatch(
            actions.updateAccountServiceCodesFeature.request({
                featureName,
                newValue,
            }),
        );
    };

    const hasAny = (...keys: (keyof DialingCodes)[]) =>
        keys.some((k) => !!dialCodes[k]);

    if (permission === PermissionType.Hidden) {
        return <AccessDenied />;
    }

    const renderContent = () => {
        if (isLoadingServiceCodes || accountDialingRule === undefined) {
            return (
                <div className={classes.loaderWrapper}>
                    <Loader dataQa="account-service-codes-loader" />
                </div>
            );
        }

        if (accountDialingRule === null) {
            return (
                <InfoBanner
                    text={t('screens:accountDashboard.dialingRulesDisabled')}
                />
            );
        }

        return (
            <Grid item className={classes.sectionsContainer}>
                {/* General */}
                <span className={classes.sectionHeader}>
                    {t('screens:callSettings.general')}
                </span>
                <Grid className={classes.rulesContainer}>
                    <RuleInput
                        label={t('screens:callSettings.transferCall')}
                        id="account-service-codes.dial_out"
                        tooltipText={t('tooltips:callSettings.transferCall')}
                        readOnly
                        value={dialCodes.dial_out || ''}
                        handleBlur={blurNoop}
                        className={classes.ruleInput}
                    />
                    <RuleInput
                        label={t('screens:callSettings.voicemail')}
                        id="account-service-codes.voicemail"
                        tooltipText={t('tooltips:callSettings.voicemail')}
                        readOnly
                        value={dialCodes.voicemail || ''}
                        handleBlur={blurNoop}
                        className={classes.ruleInput}
                    />
                    <RuleInput
                        label={t('screens:callSettings.bypassDialPlan')}
                        id="account-service-codes.bypass_dial_plan"
                        tooltipText={t('tooltips:callSettings.bypassDialPlan')}
                        readOnly
                        value={dialCodes.bypass_dial_plan || ''}
                        handleBlur={blurNoop}
                        maxLength={6}
                        className={classes.ruleInput}
                    />
                </Grid>

                {/* Call Parking — plain section, shown if any code present */}
                {hasAny('call_park', 'call_release') && (
                    <>
                        <span className={classes.sectionHeader}>
                            {t('screens:callSettings.callParking')}
                        </span>
                        <Grid className={classes.rulesContainer}>
                            <RuleInput
                                label={t('screens:callSettings.park')}
                                id="account-service-codes.call_park"
                                tooltipText={t('tooltips:callSettings.park')}
                                readOnly
                                value={dialCodes.call_park || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                            <RuleInput
                                label={t('screens:callSettings.release')}
                                id="account-service-codes.call_release"
                                tooltipText={t('tooltips:callSettings.release')}
                                readOnly
                                value={dialCodes.call_release || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                        </Grid>
                    </>
                )}

                {/* Paging/Intercom */}
                {hasAny('paging_prefix') && (
                    <>
                        <span className={classes.sectionHeader}>
                            {t('screens:callSettings.pagingIntercom')}
                        </span>
                        <Grid className={classes.rulesContainer}>
                            <RuleInput
                                label={t('screens:callSettings.pagingPrefix')}
                                id="account-service-codes.paging_prefix"
                                tooltipText={t(
                                    'tooltips:callSettings.pagingPrefix',
                                )}
                                readOnly
                                value={dialCodes.paging_prefix || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                        </Grid>
                    </>
                )}

                {/* Group Pickup */}
                {hasAny('group_pickup') && (
                    <>
                        <span className={classes.sectionHeader}>
                            {t('screens:callSettings.groupPickup')}
                        </span>
                        <Grid className={classes.rulesContainer}>
                            <RuleInput
                                label={t(
                                    'screens:callSettings.groupPickupPrefix',
                                )}
                                id="account-service-codes.group_pickup"
                                tooltipText={t(
                                    'tooltips:callSettings.groupPickupPrefix',
                                )}
                                readOnly
                                value={dialCodes.group_pickup || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                        </Grid>
                    </>
                )}

                {/* Private Call (CLIR) */}
                {clirFeature && (
                    <Grid className={classes.switchContainer}>
                        <SwitchWithLabel
                            id="clir"
                            field="clir"
                            label={t('screens:callSettings.privateCall')}
                            className={classes.switch}
                            value={clirOn}
                            disabled={isToggleDisabled(clirFeature)}
                            setValue={(_, value) =>
                                onToggleFeature(
                                    ServiceFeatureName.Clir,
                                    value,
                                )
                            }
                            skipPermission
                            dataQa="clir-toggle"
                        />
                    </Grid>
                )}
                <Grid className={classes.rulesContainer}>
                    <RuleInput
                        label={t('screens:callSettings.hideCli')}
                        id="account-service-codes.clir_hide"
                        tooltipText={t('tooltips:callSettings.hideCli')}
                        readOnly
                        value={dialCodes.clir_hide || ''}
                        handleBlur={blurNoop}
                        className={classes.ruleInput}
                    />
                    <RuleInput
                        label={t('screens:callSettings.showCli')}
                        id="account-service-codes.clir_show"
                        tooltipText={t('tooltips:callSettings.showCli')}
                        readOnly
                        value={dialCodes.clir_show || ''}
                        handleBlur={blurNoop}
                        className={classes.ruleInput}
                    />
                </Grid>

                {/* Call Recording (on-demand toggle) */}
                {callRecordingFeature && (
                    <Grid className={classes.switchContainer}>
                        <SwitchWithLabel
                            id="call-recording-on-demand"
                            field="callRecording"
                            label={t('screens:callSettings.callRecording')}
                            className={classes.switch}
                            value={callRecordingOn}
                            disabled={isToggleDisabled(callRecordingFeature)}
                            setValue={(_, value) =>
                                onToggleFeature(
                                    ServiceFeatureName.CallRecording,
                                    value,
                                )
                            }
                            skipPermission
                            dataQa="call-recording-toggle"
                        />
                    </Grid>
                )}
                {callRecordingOn && (
                    <Grid className={classes.rulesContainer}>
                        <Grid className={classes.rowInsideContainer}>
                            <RuleInput
                                label={t('screens:callSettings.startRecording')}
                                id="account-service-codes.recording_on"
                                tooltipText={t(
                                    'tooltips:callSettings.startRecording',
                                )}
                                readOnly
                                value={dialCodes.recording_on || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                            <RuleInput
                                label={t(
                                    'screens:callSettings.startRecordingDtmf',
                                )}
                                id="account-service-codes.recording_on_dtmf"
                                tooltipText={t(
                                    'tooltips:callSettings.startRecordingDtmf',
                                )}
                                readOnly
                                value={dialCodes.recording_on_dtmf || ''}
                                className={classNames(classes.ruleInput, classes.space)}
                                handleBlur={blurNoop}
                            />
                        </Grid>
                        <Grid className={classes.rowInsideContainer}>
                            <RuleInput
                                label={t('screens:callSettings.stopRecording')}
                                id="account-service-codes.recording_off"
                                tooltipText={t(
                                    'tooltips:callSettings.stopRecording',
                                )}
                                readOnly
                                value={dialCodes.recording_off || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                            <RuleInput
                                label={t(
                                    'screens:callSettings.stopRecordingDTMF',
                                )}
                                id="account-service-codes.recording_off_dtmf"
                                tooltipText={t(
                                    'tooltips:callSettings.stopRecordingDTMF',
                                )}
                                readOnly
                                value={dialCodes.recording_off_dtmf || ''}
                                className={classNames(classes.ruleInput, classes.space)}
                                handleBlur={blurNoop}
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Call Supervision — plain section */}
                {hasAny(
                    'spy',
                    'spy_dtmf',
                    'whisper',
                    'whisper_dtmf',
                    'barge_in',
                    'barge_in_dtmf',
                ) && (
                    <>
                        <span className={classes.sectionHeader}>
                            {t('screens:callSettings.callSupervision')}
                        </span>
                        <Grid className={classes.rulesContainer}>
                            <Grid className={classes.rowInsideContainer}>
                                <RuleInput
                                    label={t('screens:callSettings.spyMode')}
                                    id="account-service-codes.spy"
                                    tooltipText={t(
                                        'tooltips:callSettings.spyMode',
                                    )}
                                    readOnly
                                    value={dialCodes.spy || ''}
                                    handleBlur={blurNoop}
                                    className={classes.ruleInput}
                                />
                                <RuleInput
                                    label={t(
                                        'screens:callSettings.spyModeDtmf',
                                    )}
                                    id="account-service-codes.spy_dtmf"
                                    tooltipText={t(
                                        'tooltips:callSettings.spyModeDtmf',
                                    )}
                                    readOnly
                                    value={dialCodes.spy_dtmf || ''}
                                    className={classNames(classes.ruleInput, classes.space)}
                                    handleBlur={blurNoop}
                                />
                            </Grid>
                            <Grid className={classes.rowInsideContainer}>
                                <RuleInput
                                    label={t(
                                        'screens:callSettings.whisperMode',
                                    )}
                                    id="account-service-codes.whisper"
                                    tooltipText={t(
                                        'tooltips:callSettings.whisperMode',
                                    )}
                                    readOnly
                                    value={dialCodes.whisper || ''}
                                    handleBlur={blurNoop}
                                    className={classes.ruleInput}
                                />
                                <RuleInput
                                    label={t(
                                        'screens:callSettings.whisperModeDtmf',
                                    )}
                                    id="account-service-codes.whisper_dtmf"
                                    tooltipText={t(
                                        'tooltips:callSettings.whisperModeDtmf',
                                    )}
                                    readOnly
                                    value={dialCodes.whisper_dtmf || ''}
                                    className={classNames(classes.ruleInput, classes.space)}
                                    handleBlur={blurNoop}
                                />
                            </Grid>
                            <Grid className={classes.rowInsideContainer}>
                                <RuleInput
                                    label={t(
                                        'screens:callSettings.bargeInMode',
                                    )}
                                    id="account-service-codes.barge_in"
                                    tooltipText={t(
                                        'tooltips:callSettings.bargeInMode',
                                    )}
                                    readOnly
                                    value={dialCodes.barge_in || ''}
                                    handleBlur={blurNoop}
                                    className={classes.ruleInput}
                                />
                                <RuleInput
                                    label={t(
                                        'screens:callSettings.bargeInModeDTMF',
                                    )}
                                    id="account-service-codes.barge_in_dtmf"
                                    tooltipText={t(
                                        'tooltips:callSettings.bargeInModeDTMF',
                                    )}
                                    readOnly
                                    value={dialCodes.barge_in_dtmf || ''}
                                    className={classNames(classes.ruleInput, classes.space)}
                                    handleBlur={blurNoop}
                                />
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Call Screening (call_processing toggle) */}
                {callProcessingFeature && (
                    <Grid className={classes.switchContainer}>
                        <SwitchWithLabel
                            id="call-processing"
                            field="callProcessing"
                            label={t('screens:callSettings.callScreening')}
                            className={classes.switch}
                            value={callProcessingOn}
                            disabled={isToggleDisabled(callProcessingFeature)}
                            setValue={(_, value) =>
                                onToggleFeature(
                                    ServiceFeatureName.CallProcessing,
                                    value,
                                )
                            }
                            skipPermission
                            dataQa="call-processing-toggle"
                        />
                    </Grid>
                )}
                {callProcessingOn && (
                    <Grid className={classes.rulesContainer}>
                        <RuleInput
                            label={t(
                                'screens:callSettings.individualManagment',
                            )}
                            id="account-service-codes.cs_individual_management"
                            tooltipText={t(
                                'tooltips:callSettings.individualManagment',
                            )}
                            readOnly
                            value={dialCodes.cs_individual_management || ''}
                            handleBlur={blurNoop}
                            className={classes.ruleInput}
                        />
                        <RuleInput
                            label={t('screens:callSettings.cloudPbxManagment')}
                            id="account-service-codes.cs_bulk_management"
                            tooltipText={t(
                                'tooltips:callSettings.cloudPBXManagment',
                            )}
                            readOnly
                            value={dialCodes.cs_bulk_management || ''}
                            handleBlur={blurNoop}
                            className={classes.ruleInput}
                        />
                    </Grid>
                )}

                {/* Ring Group login/logout */}
                {hasAny('huntgroup_login', 'huntgroup_logout') && (
                    <>
                        <span className={classes.sectionHeader}>
                            {t('screens:callSettings.ringGroupLoginLogout')}
                        </span>
                        <Grid className={classes.rulesContainer}>
                            <RuleInput
                                label={t(
                                    'screens:callSettings.loginToRingGroup',
                                )}
                                id="account-service-codes.huntgroup_login"
                                tooltipText={t(
                                    'tooltips:callSettings.ringGroupLogin',
                                )}
                                readOnly
                                value={dialCodes.huntgroup_login || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                            <RuleInput
                                label={t(
                                    'screens:callSettings.logoutOfRingGroup',
                                )}
                                id="account-service-codes.huntgroup_logout"
                                tooltipText={t(
                                    'tooltips:callSettings.ringGroupLogout',
                                )}
                                readOnly
                                value={dialCodes.huntgroup_logout || ''}
                                handleBlur={blurNoop}
                                className={classes.ruleInput}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        );
    };

    return (
        <PermissionProvider
            permission={AccountPermission.ServiceCodes.value}
        >
            <div className={classes.mainDashboardContainer}>
                <div className={classes.header}>
                    <Header
                        title={t<string>(
                            'screens:accountDashboard.myServiceCodes',
                        )}
                        dataQa="account-service-codes-header"
                        showDetails={false}
                        hideTotal={true}
                    />
                    <div className={classes.rightSection}>
                        <AccountControlPanel />
                    </div>
                </div>

                <div className={classes.scrollable}>
                    <div
                        className={classNames(classes.contentContainer)}
                    >
                        {renderContent()}
                    </div>
                </div>
            </div>
        </PermissionProvider>
    );
};

export default AccountServiceCodes;